import type Phaser from 'phaser';

/**
 * **A composição visual do Turbilhão de Lâminas e da Lança Solar** (V28H.N).
 *
 * ## Porque este ficheiro existe
 *
 * A primeira tentativa destes dois efeitos foi **reprovada pelo dono**, e com
 * razão: uma faixa rectangular a atravessar o ecrã e um traço a girar não são
 * um círculo mágico nem uma lança solar - são a geometria da simulação pintada
 * a direito. O contrato aprovado descreve composições, e uma composição não sai
 * de um `Rectangle`.
 *
 * O que ele desenha está escrito no contrato, item a item:
 *
 * **Turbilhão** — círculo mágico centrado na nave; circunferência interna
 * transparente, grande o bastante para conter nave e canhões sem os cobrir;
 * anéis concêntricos de energia; símbolos abstractos distribuídos pelo círculo;
 * símbolos e anéis a girar em sentidos alternados; uma lâmina enorme, neon, a
 * girar à volta da nave, que **nunca** ultrapassa o limite externo; o círculo
 * comunica exactamente a área atingida; três camadas na lâmina - núcleo quase
 * branco, halo neon ciano/violeta e resíduos curtos de energia.
 *
 * **Lança Solar** — núcleo fino branco-incandescente; halo dourado/âmbar e
 * bordas solares alaranjadas; corona pulsante na boca da nave; arcos, faíscas e
 * ondas de calor durante a carga; começo e extremidade com acabamento, nunca um
 * rectângulo uniforme; a espessura comunica o nível de carga; antecipação, pico
 * e dissipação.
 *
 * ## O que ele NÃO decide
 *
 * Nada. O raio do círculo, o comprimento do feixe, a sua espessura e a força da
 * carga chegam de `HuntCardVisuals`, que a simulação produz a partir dos mesmos
 * números com que ela cobra. Este ficheiro escolhe cores, camadas e o ritmo da
 * animação - e um número mecânico escolhido aqui seria um círculo a prometer
 * uma área que não é a que fere.
 *
 * ## O orçamento
 *
 * As texturas são **procedurais e criadas uma vez** por `Phaser.Game`; os
 * objectos são criados à primeira vez que fazem falta e depois **escondidos**,
 * nunca destruídos e recriados. `update` não aloca: as faíscas e os resíduos são
 * conjuntos fixos, reposicionados a partir do relógio da cena. `destroy()`
 * esvazia tudo o que a classe criou.
 *
 * `blendAdd` chega por parâmetro em vez de um `import` do motor, pela mesma
 * razão de `pvp-arena-vfx.ts`: importar `phaser` como VALOR arranca o registo
 * de renderers inteiro, que num ambiente sem canvas rebenta antes de a primeira
 * prova correr.
 */

/** As chaves das texturas geradas. Um prefixo, para não colidir com arte real. */
const TEXTURE = {
  circleRings: 'card-fx-circle-rings',
  circleGlyphs: 'card-fx-circle-glyphs',
  sawCore: 'card-fx-saw-core',
  sawGlow: 'card-fx-saw-glow',
  slash: 'card-fx-slash',
  beamCore: 'card-fx-beam-core',
  beamHalo: 'card-fx-beam-halo',
  beamEdge: 'card-fx-beam-edge',
  corona: 'card-fx-corona',
  spark: 'card-fx-spark',
  heatRing: 'card-fx-heat-ring',
} as const;

/** O lado das texturas circulares. Uma vez só, e escalado depois. */
const CIRCLE_TEX = 512;

/** O comprimento das texturas de feixe e lâmina, em píxeis de textura. */
const BAR_TEX = 512;

/**
 * **O vazio entre a serra e a nave**, em fracção do raio externo.
 *
 * Na referência do dono é o disco cinzento do meio: a serra não encosta à nave,
 * e o intervalo é transparente. Ele também é o que deixa a nave e os dois
 * canhões legíveis lá dentro, que continua a ser contrato.
 *
 * **PROVISÓRIO**, como todo o equilíbrio visual deste gate.
 */
const SAW_INNER_FACTOR = 0.32;

/**
 * E até onde as pontas chegam, na mesma medida.
 *
 * Menos de um: a lâmina "nunca ultrapassa o limite externo do círculo", e
 * derivar as pontas do mesmo raio é o que o garante sem uma verificação que
 * alguém tenha de lembrar-se de fazer.
 */
const SAW_OUTER_FACTOR = 0.87;

/** Quantas lâminas a serra tem. Várias, e não uma - é a correcção do dono. */
const SAW_BLADES = 10;

/** Quanto cada lâmina se curva, em radianos, da base à ponta. */
const SAW_CURVE = 0.95;

/** A meia-largura angular da base de cada lâmina. */
const SAW_BASE_HALF_WIDTH = 0.2;

/** Os períodos de rotação, em ms. Sentidos ALTERNADOS, que é o contrato. */
const RING_PERIOD_MS = 9_000;
const GLYPH_PERIOD_MS = 6_200;
/** A serra gira depressa: ela é a coisa que corta, e não a moldura. */
const SAW_PERIOD_MS = 1_100;

/** Quantas faíscas a carga tem. Conjunto FIXO, reposicionado por quadro. */
const SPARKS = 10;

/** Quanto tempo o feixe leva a dissipar-se depois de acabar, em ms. */
const BEAM_FADE_MS = 220;

/** E quanto tempo dura o pico, logo a seguir a acender. */
const BEAM_PEAK_MS = 140;

/** Um gerador determinístico, para as faíscas serem sempre as mesmas. */
function mulberry(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d_2b_79_f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/**
 * Os anéis concêntricos e as marcas do círculo mágico.
 *
 * Desenhado numa textura e não em `Graphics` por quadro: um círculo mágico tem
 * dezenas de traços, e redesenhá-los sessenta vezes por segundo seria trabalho
 * por quadro para uma coisa que só roda.
 */
function makeCircleRings(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const texture = scene.textures.createCanvas(key, CIRCLE_TEX, CIRCLE_TEX);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  const middle = CIRCLE_TEX / 2;
  const outer = middle - 8;
  context.clearRect(0, 0, CIRCLE_TEX, CIRCLE_TEX);
  context.lineCap = 'round';
  /*
   * **Branco, e só branco.**
   *
   * A cor é do JOGADOR (gate V28H.O), e um sprite branco aceita qualquer tinta.
   * Pintar a cor aqui obrigaria a uma textura por cor, e uma escolha livre
   * passaria a ser uma lista de escolhas que alguém teve de prever.
   */
  const glow = (blur: number, width: number, alpha: number, radius: number): void => {
    context.save();
    context.shadowColor = 'rgba(255,255,255,0.95)';
    context.shadowBlur = blur;
    context.strokeStyle = `rgba(255,255,255,${alpha})`;
    context.lineWidth = width;
    context.beginPath();
    context.arc(middle, middle, radius, 0, Math.PI * 2);
    context.stroke();
    context.restore();
  };

  /*
   * DOIS anéis colados, e nada mais lá dentro.
   *
   * É a referência do dono, e é deliberadamente vazio: o interior é onde a nave
   * vive, e um interior cheio de anéis tornaria a nave difícil de ler - que é
   * exactamente o que o contrato proíbe.
   */
  glow(14, 5, 0.95, outer);
  glow(8, 2.5, 0.7, outer - 16);
  texture.refresh();
}

/**
 * Os símbolos abstractos, no seu próprio anel.
 *
 * Numa textura separada da dos anéis porque eles giram em sentido CONTRÁRIO, e
 * duas rotações opostas precisam de dois objectos. São abstractos de propósito:
 * inventar um alfabeto seria inventar conteúdo, e o que o contrato pede são
 * "símbolos abstratos".
 */
function makeCircleGlyphs(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const texture = scene.textures.createCanvas(key, CIRCLE_TEX, CIRCLE_TEX);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  const middle = CIRCLE_TEX / 2;
  const outer = middle - 6;
  /* Na coroa entre os dois anéis, que é onde a referência os põe. */
  const ring = outer - 8;
  const random = mulberry(0x2b_7f);
  context.clearRect(0, 0, CIRCLE_TEX, CIRCLE_TEX);
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.lineWidth = 3;

  const COUNT = 22;
  for (let index = 0; index < COUNT; index += 1) {
    const angle = (index / COUNT) * Math.PI * 2;
    const cx = middle + Math.cos(angle) * ring;
    const cy = middle + Math.sin(angle) * ring;
    const size = outer * 0.075;
    context.strokeStyle = index % 2 === 0 ? 'rgba(214,246,255,0.9)' : 'rgba(198,166,255,0.85)';
    context.save();
    context.translate(cx, cy);
    /* Cada símbolo aponta para fora, que é o que os faz ler como um anel. */
    context.rotate(angle + Math.PI / 2);
    context.beginPath();
    /*
     * Três a cinco segmentos por símbolo, de um gerador com semente fixa: eles
     * são sempre os mesmos em todas as máquinas e em todas as corridas, o que
     * os torna arte e não ruído.
     */
    const strokes = 3 + Math.floor(random() * 3);
    for (let stroke = 0; stroke < strokes; stroke += 1) {
      const x1 = (random() - 0.5) * size * 2;
      const y1 = (random() - 0.5) * size * 2;
      const x2 = (random() - 0.5) * size * 2;
      const y2 = (random() - 0.5) * size * 2;
      context.moveTo(x1, y1);
      context.lineTo(x2, y2);
    }
    context.stroke();
    /* E um ponto no meio, que dá peso ao símbolo. */
    context.fillStyle = 'rgba(255,255,255,0.85)';
    context.beginPath();
    context.arc(0, 0, size * 0.16, 0, Math.PI * 2);
    context.fill();
    context.restore();
  }
  texture.refresh();
}

/**
 * Uma barra com ACABAMENTO nas duas pontas.
 *
 * O contrato é literal sobre isto - "início e extremidade com acabamento, nunca
 * um retângulo uniforme" - e é a razão de a lâmina e o feixe serem texturas em
 * vez de `Rectangle`: um rectângulo tem exactamente as pontas que o contrato
 * proíbe.
 *
 * O perfil é o mesmo nos dois: afina em direcção às pontas ao longo do
 * comprimento, e esbate do centro para as bordas ao longo da espessura.
 */
function makeTaperedBar(
  scene: Phaser.Scene,
  key: string,
  options: {
    readonly height: number;
    readonly inner: string;
    readonly outer: string;
    /** Onde o corpo está mais grosso, de 0 (início) a 1 (ponta). */
    readonly peak: number;
    /** Quão afiada é a ponta: 1 é um corte recto, 3 é uma agulha. */
    readonly sharpness: number;
  },
): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const height = options.height;
  const texture = scene.textures.createCanvas(key, BAR_TEX, height);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, BAR_TEX, height);
  const middle = height / 2;
  /*
   * Coluna a coluna, e não um gradiente de duas dimensões: o canvas não tem um
   * gradiente que estreite, e desenhar por colunas dá o perfil exacto com um
   * ciclo que corre uma vez na vida do jogo.
   */
  for (let x = 0; x < BAR_TEX; x += 1) {
    const t = x / (BAR_TEX - 1);
    /* Sobe depressa do início até ao pico, e afina até à ponta. */
    const rise = Math.min(1, t / Math.max(0.0001, options.peak));
    const fall = Math.min(1, (1 - t) / Math.max(0.0001, 1 - options.peak));
    const profile = rise ** 0.55 * fall ** options.sharpness;
    const half = middle * profile;
    if (half <= 0.5) {
      continue;
    }
    const gradient = context.createLinearGradient(0, middle - half, 0, middle + half);
    gradient.addColorStop(0, options.outer);
    gradient.addColorStop(0.5, options.inner);
    gradient.addColorStop(1, options.outer);
    context.fillStyle = gradient;
    context.fillRect(x, middle - half, 1, half * 2);
  }
  texture.refresh();
}

/** Um disco de luz, quase branco no meio e transparente na borda. */
function makeRadialGlow(
  scene: Phaser.Scene,
  key: string,
  stops: readonly (readonly [number, string])[],
  size = 256,
): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const gradient = context.createRadialGradient(middle, middle, 0, middle, middle, middle);
  for (const [offset, colour] of stops) {
    gradient.addColorStop(offset, colour);
  }
  context.fillStyle = gradient;
  context.fillRect(0, 0, size, size);
  texture.refresh();
}

/** Um anel fino, para a onda de calor que se expande e apaga. */
function makeHeatRing(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 256;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  context.strokeStyle = 'rgba(255,196,110,0.85)';
  context.lineWidth = 7;
  context.beginPath();
  context.arc(middle, middle, middle - 10, 0, Math.PI * 2);
  context.stroke();
  context.strokeStyle = 'rgba(255,150,60,0.35)';
  context.lineWidth = 14;
  context.beginPath();
  context.arc(middle, middle, middle - 22, 0, Math.PI * 2);
  context.stroke();
  texture.refresh();
}

/** Um corte curto, para marcar um inimigo apanhado pela lâmina. */
function makeSlash(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const width = 192;
  const height = 96;
  const texture = scene.textures.createCanvas(key, width, height);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, width, height);
  context.lineCap = 'round';
  /* Um arco, e não uma linha: um corte tem curvatura, e é o que o lê como corte. */
  for (const [lineWidth, colour] of [
    [14, 'rgba(155,231,255,0.35)'],
    [6, 'rgba(226,248,255,0.95)'],
  ] as const) {
    context.strokeStyle = colour;
    context.lineWidth = lineWidth;
    context.beginPath();
    context.moveTo(12, height * 0.78);
    context.quadraticCurveTo(width / 2, -height * 0.25, width - 12, height * 0.78);
    context.stroke();
  }
  texture.refresh();
}

/**
 * **A SERRA: várias lâminas, e não uma** (gate V28H.O).
 *
 * ## A correcção do dono, desenhada
 *
 * A primeira serra era uma lâmina só, e foi reprovada. O que ele desenhou é um
 * conjunto de lâminas curvas a irradiar de um centro vazio - cada uma larga na
 * base, a curvar no sentido da rotação e a afinar até uma ponta - com **furos
 * transparentes** nas lâminas, e um intervalo transparente entre o conjunto e a
 * nave.
 *
 * ## Como cada lâmina é construída
 *
 * Por amostragem ao longo dela: o raio cresce da base à ponta, o ângulo
 * central curva-se, e a meia-largura angular afina até zero. O caminho sobe por
 * um dos bordos e desce pelo outro, e o preenchimento faz a foice.
 *
 * Os furos são abertos com `destination-out` DEPOIS de tudo o resto: assim eles
 * levam também o brilho por baixo, e o que fica é transparência a sério - e não
 * um ponto escuro pintado por cima.
 *
 * ## Branca, sempre
 *
 * A cor é escolhida pelo jogador e aplicada como TINTA. Uma textura por cor
 * seria uma lista de cores que alguém teve de prever.
 */
function makeSaw(
  scene: Phaser.Scene,
  key: string,
  options: { readonly blur: number; readonly alpha: number; readonly grow: number },
): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const texture = scene.textures.createCanvas(key, CIRCLE_TEX, CIRCLE_TEX);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  const middle = CIRCLE_TEX / 2;
  /*
   * A textura é desenhada para o raio EXTERNO do círculo, e a cena escala-a por
   * ele. Assim as pontas caem sempre na mesma fracção do limite, seja qual for o
   * tamanho do casco.
   */
  const span = middle - 4;
  const inner = span * SAW_INNER_FACTOR;
  const outerTip = span * SAW_OUTER_FACTOR;
  context.clearRect(0, 0, CIRCLE_TEX, CIRCLE_TEX);

  const point = (angle: number, radius: number): readonly [number, number] => [
    middle + Math.cos(angle) * radius,
    middle + Math.sin(angle) * radius,
  ];

  const SAMPLES = 26;
  context.save();
  context.fillStyle = `rgba(255,255,255,${options.alpha})`;
  if (options.blur > 0) {
    context.shadowColor = 'rgba(255,255,255,0.9)';
    context.shadowBlur = options.blur;
  }
  for (let blade = 0; blade < SAW_BLADES; blade += 1) {
    const base = (blade / SAW_BLADES) * Math.PI * 2;
    context.beginPath();
    for (let sample = 0; sample <= SAMPLES; sample += 1) {
      const t = sample / SAMPLES;
      const radius = inner + (outerTip - inner) * t;
      /* A curvatura cresce depressa perto da base: é o que dá o gancho. */
      const angle = base + SAW_CURVE * t ** 0.7;
      /*
       * A largura afina até zero na ponta, e a potência acima de um faz a foice
       * manter corpo a meio em vez de estreitar em linha recta.
       */
      const half = SAW_BASE_HALF_WIDTH * (1 - t) ** 1.25 * options.grow;
      const [x, y] = point(angle + half, radius);
      if (sample === 0) {
        context.moveTo(x, y);
      } else {
        context.lineTo(x, y);
      }
    }
    for (let sample = SAMPLES; sample >= 0; sample -= 1) {
      const t = sample / SAMPLES;
      const radius = inner + (outerTip - inner) * t;
      const angle = base + SAW_CURVE * t ** 0.7;
      const half = SAW_BASE_HALF_WIDTH * (1 - t) ** 1.25 * options.grow;
      const [x, y] = point(angle - half, radius);
      context.lineTo(x, y);
    }
    context.closePath();
    context.fill();
  }
  context.restore();

  /*
   * **Os furos**, um por lâmina, perto da base e onde ela é mais larga.
   *
   * `destination-out` apaga o que está por baixo em vez de pintar por cima -
   * portanto o que fica é um buraco transparente, através do qual se vê a arena.
   * Era isso que os pontos pretos da referência representavam.
   */
  context.save();
  context.globalCompositeOperation = 'destination-out';
  context.fillStyle = 'rgba(0,0,0,1)';
  for (let blade = 0; blade < SAW_BLADES; blade += 1) {
    const base = (blade / SAW_BLADES) * Math.PI * 2;
    const t = 0.16;
    const radius = inner + (outerTip - inner) * t;
    const angle = base + SAW_CURVE * t ** 0.7;
    const [x, y] = point(angle, radius);
    const hole = span * 0.035 * options.grow;
    context.beginPath();
    context.arc(x, y, hole, 0, Math.PI * 2);
    context.fill();
  }
  context.restore();
  texture.refresh();
}

/** Cria as texturas, uma vez por `Phaser.Game`. */
export function ensureCardFxTextures(scene: Phaser.Scene): void {
  makeCircleRings(scene, TEXTURE.circleRings);
  makeCircleGlyphs(scene, TEXTURE.circleGlyphs);
  makeSlash(scene, TEXTURE.slash);
  /*
   * A serra em DUAS camadas.
   *
   * O núcleo é nítido e quase opaco; o halo é a mesma forma, mais gorda, muito
   * borrada e fraca, desenhada por baixo e em `ADD`. É assim que se faz néon:
   * uma forma dura sobre uma auréola difusa da mesma cor.
   */
  makeSaw(scene, TEXTURE.sawCore, { blur: 10, alpha: 0.95, grow: 1 });
  makeSaw(scene, TEXTURE.sawGlow, { blur: 46, alpha: 0.4, grow: 1.35 });
  /* O feixe: três camadas, do núcleo incandescente às bordas solares. */
  makeTaperedBar(scene, TEXTURE.beamCore, {
    height: 20,
    inner: 'rgba(255,255,255,1)',
    outer: 'rgba(255,246,214,0.2)',
    peak: 0.25,
    sharpness: 0.85,
  });
  makeTaperedBar(scene, TEXTURE.beamHalo, {
    height: 72,
    inner: 'rgba(255,214,120,0.75)',
    outer: 'rgba(255,170,60,0.05)',
    peak: 0.22,
    sharpness: 0.7,
  });
  makeTaperedBar(scene, TEXTURE.beamEdge, {
    height: 148,
    inner: 'rgba(255,140,40,0.34)',
    outer: 'rgba(255,90,20,0)',
    peak: 0.2,
    sharpness: 0.6,
  });
  makeRadialGlow(scene, TEXTURE.corona, [
    [0, 'rgba(255,255,255,0.98)'],
    [0.28, 'rgba(255,224,150,0.8)'],
    [0.62, 'rgba(255,150,50,0.32)'],
    [1, 'rgba(255,110,20,0)'],
  ]);
  makeRadialGlow(
    scene,
    TEXTURE.spark,
    [
      [0, 'rgba(255,255,255,1)'],
      [0.45, 'rgba(255,214,130,0.7)'],
      [1, 'rgba(255,150,50,0)'],
    ],
    64,
  );
  makeHeatRing(scene, TEXTURE.heatRing);
}

/** O que a cena entrega ao círculo, por quadro. */
export interface MagicCircleState {
  readonly x: number;
  readonly y: number;
  /** O raio a que a lâmina fere. É ele que o limite externo desenha. */
  readonly radius: number;
  /** Falso é a moldura passiva a girar; verdadeiro é a serra a cortar. */
  readonly active: boolean;
  /** A cor escolhida pelo jogador, como inteiro `0xRRGGBB`. */
  readonly tint: number;
}

/**
 * **O círculo mágico do Turbilhão de Lâminas** (gates V28H.N e V28H.O).
 *
 * ## Passivo, e depois cortante
 *
 * O círculo aparece assim que a carta está equipada e fica a girar para sempre:
 * é ele que diz que aquela nave tem a carta, e é ele que comunica a área que
 * vai ferir. A activação acrescenta a SERRA lá dentro - várias lâminas curvas,
 * néon, com furos transparentes e um intervalo transparente até ao casco.
 *
 * ## A cor é do jogador
 *
 * As texturas são brancas e recebem uma TINTA. É o mesmo objecto e a mesma
 * geometria em qualquer cor - e a escolha é livre em vez de uma lista que
 * alguém teve de prever.
 */
export class MagicCircleFx {
  private readonly scene: Phaser.Scene;
  private readonly blendAdd: number;
  private readonly depth: number;
  private rings: Phaser.GameObjects.Image | null = null;
  private glyphs: Phaser.GameObjects.Image | null = null;
  private glow: Phaser.GameObjects.Image | null = null;
  private core: Phaser.GameObjects.Image | null = null;
  private readonly slashes: Phaser.GameObjects.Image[] = [];

  constructor(scene: Phaser.Scene, blendAdd: number, depth: number) {
    this.scene = scene;
    this.blendAdd = blendAdd;
    this.depth = depth;
  }

  /**
   * Desenha o círculo, ou esconde-o quando não há carta nenhuma.
   *
   * `nowMs` é o relógio da CENA e nunca o tique da simulação: a rotação é
   * decoração, e a simulação apanha toda a gente ao alcance seja qual for o
   * ângulo desenhado. Ligá-la ao tique faria uma decoração parecer uma regra.
   */
  update(state: MagicCircleState | null, nowMs: number): void {
    if (state === null) {
      this.rings?.setVisible(false);
      this.glyphs?.setVisible(false);
      this.glow?.setVisible(false);
      this.core?.setVisible(false);
      return;
    }
    this.build();
    const { x, y, radius, tint } = state;
    const diameter = radius * 2;

    /* Os anéis e os símbolos giram em sentidos ALTERNADOS. Contrato. */
    this.rings
      ?.setVisible(true)
      .setPosition(x, y)
      .setDisplaySize(diameter, diameter)
      .setTint(tint)
      .setRotation(((nowMs % RING_PERIOD_MS) / RING_PERIOD_MS) * Math.PI * 2);
    this.glyphs
      ?.setVisible(true)
      .setPosition(x, y)
      .setDisplaySize(diameter, diameter)
      .setTint(tint)
      .setRotation(-((nowMs % GLYPH_PERIOD_MS) / GLYPH_PERIOD_MS) * Math.PI * 2);

    if (!state.active) {
      /* A moldura sozinha: a carta está equipada e não está a cortar. */
      this.glow?.setVisible(false);
      this.core?.setVisible(false);
      return;
    }

    /*
     * A SERRA, dentro do círculo e nunca para lá dele.
     *
     * A textura já é desenhada até `SAW_OUTER_FACTOR` do seu próprio raio,
     * portanto escalá-la pelo diâmetro do círculo põe as pontas exactamente onde
     * o contrato as quer - e nenhuma verificação por quadro pode falhar-se.
     */
    const spin = ((nowMs % SAW_PERIOD_MS) / SAW_PERIOD_MS) * Math.PI * 2;
    /* Um pulsar curto: uma serra que não respira lê-se como um autocolante. */
    const breathe = 1 + Math.sin(nowMs / 130) * 0.02;
    this.glow
      ?.setVisible(true)
      .setPosition(x, y)
      .setDisplaySize(diameter * breathe, diameter * breathe)
      .setTint(tint)
      .setRotation(spin);
    this.core
      ?.setVisible(true)
      .setPosition(x, y)
      .setDisplaySize(diameter * breathe, diameter * breathe)
      .setTint(tint)
      .setRotation(spin);
  }

  /**
   * Um corte curto sobre um inimigo apanhado.
   *
   * Do mesmo conjunto fixo, reutilizado: um `tween` por acerto numa serra a
   * bater cinco vezes por segundo seria um `tween` por acerto.
   */
  cut(x: number, y: number, size: number, nowMs: number, tint: number): void {
    this.build();
    const slash = this.slashes.find((piece) => !piece.visible) ?? this.slashes[0];
    if (!slash) {
      return;
    }
    slash
      .setVisible(true)
      .setPosition(x, y)
      .setDisplaySize(size * 1.6, size * 0.8)
      .setTint(tint)
      /* Uma orientação por acerto, do relógio: dois cortes seguidos não são iguais. */
      .setRotation(((nowMs % 360) / 360) * Math.PI * 2)
      .setAlpha(1);
    this.scene.tweens.add({
      targets: slash,
      alpha: 0,
      duration: 180,
      onComplete: () => slash.setVisible(false),
    });
  }

  private build(): void {
    if (this.rings) {
      return;
    }
    ensureCardFxTextures(this.scene);
    this.rings = this.scene.add
      .image(0, 0, TEXTURE.circleRings)
      .setDepth(this.depth)
      .setBlendMode(this.blendAdd)
      .setAlpha(0.9);
    this.glyphs = this.scene.add
      .image(0, 0, TEXTURE.circleGlyphs)
      .setDepth(this.depth)
      .setBlendMode(this.blendAdd)
      .setAlpha(0.85);
    /* A auréola por BAIXO do núcleo: é essa ordem que faz néon. */
    this.glow = this.scene.add
      .image(0, 0, TEXTURE.sawGlow)
      .setDepth(this.depth + 1)
      .setBlendMode(this.blendAdd)
      .setAlpha(0.95)
      .setVisible(false);
    this.core = this.scene.add
      .image(0, 0, TEXTURE.sawCore)
      .setDepth(this.depth + 2)
      .setBlendMode(this.blendAdd)
      .setVisible(false);
    for (let index = 0; index < 6; index += 1) {
      this.slashes.push(
        this.scene.add
          .image(0, 0, TEXTURE.slash)
          .setDepth(this.depth + 3)
          .setBlendMode(this.blendAdd)
          .setVisible(false),
      );
    }
  }

  destroy(): void {
    for (const piece of [this.rings, this.glyphs, this.glow, this.core]) {
      piece?.destroy();
    }
    this.rings = null;
    this.glyphs = null;
    this.glow = null;
    this.core = null;
    for (const piece of this.slashes) {
      piece.destroy();
    }
    this.slashes.length = 0;
  }
}

/** O que a cena entrega à lança, por quadro. */
export interface SolarLanceState {
  /** A carga em curso: onde ela acontece, e quanto já foi cumprido. */
  readonly charge: { readonly x: number; readonly y: number; readonly progress: number } | null;
  /** O feixe a arder, com a boca, o ângulo, o comprimento e a espessura. */
  readonly beam: {
    readonly x: number;
    readonly y: number;
    readonly angle: number;
    readonly length: number;
    readonly width: number;
  } | null;
  /** O raio do casco, para a corona ter uma escala que não é um número solto. */
  readonly hullRadius: number;
}

/**
 * **A Lança Solar: carga, feixe e dissipação.**
 *
 * Três momentos, e o contrato nomeia os três: antecipação - a corona a crescer
 * com arcos, faíscas e ondas de calor; pico - o instante em que o feixe acende,
 * mais largo e mais claro do que o resto da queima; dissipação - o feixe a
 * apagar em vez de desaparecer entre dois quadros.
 */
export class SolarLanceFx {
  private readonly scene: Phaser.Scene;
  private readonly blendAdd: number;
  private readonly depth: number;
  private corona: Phaser.GameObjects.Image | null = null;
  private heat: Phaser.GameObjects.Image | null = null;
  private edge: Phaser.GameObjects.Image | null = null;
  private halo: Phaser.GameObjects.Image | null = null;
  private core: Phaser.GameObjects.Image | null = null;
  private tip: Phaser.GameObjects.Image | null = null;
  private readonly sparks: Phaser.GameObjects.Image[] = [];
  /** Quando o feixe acendeu, para o pico. `null` enquanto não há feixe. */
  private litAtMs: number | null = null;
  /** Quando ele acabou, para a dissipação, e com que forma ele acabou. */
  private fadeFromMs: number | null = null;
  private last: SolarLanceState['beam'] = null;

  constructor(scene: Phaser.Scene, blendAdd: number, depth: number) {
    this.scene = scene;
    this.blendAdd = blendAdd;
    this.depth = depth;
  }

  update(state: SolarLanceState, nowMs: number): void {
    if (state.charge === null && state.beam === null && this.fadeFromMs === null) {
      this.hideAll();
      return;
    }
    this.build();
    this.drawCharge(state, nowMs);
    this.drawBeam(state, nowMs);
  }

  /**
   * A CARGA: uma corona que cresce na boca, faíscas e uma onda de calor.
   *
   * A corona cresce com o progresso e pulsa: um jogador tem de conseguir ler,
   * pelo canto do olho, quanto é que já carregou - é o que transforma "segurar a
   * tecla" numa decisão em vez de uma espera.
   */
  private drawCharge(state: SolarLanceState, nowMs: number): void {
    const charge = state.charge;
    if (charge === null) {
      this.corona?.setVisible(false);
      this.heat?.setVisible(false);
      for (const spark of this.sparks) {
        spark.setVisible(false);
      }
      return;
    }
    const pulse = 1 + Math.sin(nowMs / 60) * 0.07;
    const size = state.hullRadius * (0.7 + charge.progress * 1.5) * pulse;
    this.corona
      ?.setVisible(true)
      .setPosition(charge.x, charge.y)
      .setDisplaySize(size * 2, size * 2)
      .setAlpha(0.35 + charge.progress * 0.6);

    /*
     * A ONDA DE CALOR: um anel que se expande e apaga, em ciclo.
     *
     * Um objecto só, com a fase derivada do relógio - e não uma partícula por
     * onda. O ciclo acelera com a carga, que é a mesma leitura da corona por
     * outro canal.
     */
    const period = 520 - charge.progress * 220;
    const phase = (nowMs % period) / period;
    const ringSize = state.hullRadius * (0.9 + phase * 2.6);
    this.heat
      ?.setVisible(true)
      .setPosition(charge.x, charge.y)
      .setDisplaySize(ringSize * 2, ringSize * 2)
      .setAlpha((1 - phase) * (0.15 + charge.progress * 0.35));

    /*
     * As FAÍSCAS e os arcos: um conjunto fixo, a orbitar para dentro.
     *
     * Cada uma tem a sua fase e o seu raio, do gerador com semente - portanto
     * elas são sempre as mesmas, e nada é alocado por quadro. Elas caem para
     * dentro da boca, que é o que lê como energia a ser reunida em vez de
     * energia a escapar.
     */
    for (const [index, spark] of this.sparks.entries()) {
      const seedAngle = (index / SPARKS) * Math.PI * 2;
      const speed = 0.0016 + (index % 3) * 0.0007;
      const cycle = (nowMs * speed + index * 0.37) % 1;
      const distance = state.hullRadius * (2.4 - cycle * 2.0);
      const angle = seedAngle + cycle * 2.6;
      const dot = state.hullRadius * (0.16 + (index % 2) * 0.07) * (1 - cycle * 0.4);
      spark
        .setVisible(true)
        .setPosition(charge.x + Math.cos(angle) * distance, charge.y + Math.sin(angle) * distance)
        .setDisplaySize(dot * 2, dot * 2)
        .setAlpha((0.25 + charge.progress * 0.7) * (1 - Math.abs(cycle - 0.5) * 0.9));
    }
  }

  /** O FEIXE: três camadas, um pico ao acender e uma dissipação ao acabar. */
  private drawBeam(state: SolarLanceState, nowMs: number): void {
    const beam = state.beam;
    if (beam !== null) {
      if (this.litAtMs === null) {
        this.litAtMs = nowMs;
      }
      this.fadeFromMs = null;
      this.last = beam;
    } else if (this.litAtMs !== null) {
      /* Acabou agora: começa a dissipar-se em vez de desaparecer. */
      this.litAtMs = null;
      this.fadeFromMs = nowMs;
    }

    const shape = beam ?? this.last;
    if (shape === null) {
      this.hideBeam();
      return;
    }

    let scale = 1;
    let alpha = 1;
    if (beam !== null && this.litAtMs !== null) {
      /* O PICO: mais largo e mais claro nos primeiros instantes. */
      const since = nowMs - this.litAtMs;
      const peak = Math.max(0, 1 - since / BEAM_PEAK_MS);
      scale = 1 + peak * 0.75;
    } else if (this.fadeFromMs !== null) {
      const since = nowMs - this.fadeFromMs;
      if (since >= BEAM_FADE_MS) {
        this.fadeFromMs = null;
        this.last = null;
        this.hideBeam();
        return;
      }
      /* A DISSIPAÇÃO: encolhe e apaga, e não corta. */
      const left = 1 - since / BEAM_FADE_MS;
      scale = left;
      alpha = left;
    }

    const width = shape.width * scale;
    for (const [layer, factor, layerAlpha] of [
      [this.edge, 2.6, 0.55],
      [this.halo, 1.35, 0.85],
      [this.core, 0.42, 1],
    ] as const) {
      layer
        ?.setVisible(true)
        .setPosition(shape.x, shape.y)
        .setDisplaySize(shape.length, Math.max(2, width * factor))
        .setRotation(shape.angle)
        .setAlpha(layerAlpha * alpha);
    }

    /*
     * O IMPACTO na extremidade alcançada.
     *
     * O contrato pede "impacto visível no alvo ou no limite alcançado", e o
     * limite é o que a simulação garante: o feixe cobra até ao seu comprimento e
     * nem um pixel mais. Desenhar no alvo exigiria escolher um alvo aqui, que é
     * uma decisão da simulação e não do renderer.
     */
    const tipSize = Math.max(shape.width, state.hullRadius * 0.5) * 2.4 * scale;
    this.tip
      ?.setVisible(true)
      .setPosition(
        shape.x + Math.cos(shape.angle) * shape.length,
        shape.y + Math.sin(shape.angle) * shape.length,
      )
      .setDisplaySize(tipSize, tipSize)
      .setAlpha((0.55 + Math.sin(nowMs / 45) * 0.15) * alpha);
  }

  private hideBeam(): void {
    this.edge?.setVisible(false);
    this.halo?.setVisible(false);
    this.core?.setVisible(false);
    this.tip?.setVisible(false);
  }

  private hideAll(): void {
    this.corona?.setVisible(false);
    this.heat?.setVisible(false);
    for (const spark of this.sparks) {
      spark.setVisible(false);
    }
    this.hideBeam();
  }

  private build(): void {
    if (this.core) {
      return;
    }
    ensureCardFxTextures(this.scene);
    this.heat = this.scene.add
      .image(0, 0, TEXTURE.heatRing)
      .setDepth(this.depth)
      .setBlendMode(this.blendAdd)
      .setVisible(false);
    for (let index = 0; index < SPARKS; index += 1) {
      this.sparks.push(
        this.scene.add
          .image(0, 0, TEXTURE.spark)
          .setDepth(this.depth + 1)
          .setBlendMode(this.blendAdd)
          .setVisible(false),
      );
    }
    /* As camadas do feixe, de fora para dentro: a ordem é a profundidade. */
    this.edge = this.scene.add
      .image(0, 0, TEXTURE.beamEdge)
      .setOrigin(0, 0.5)
      .setDepth(this.depth + 2)
      .setBlendMode(this.blendAdd)
      .setVisible(false);
    this.halo = this.scene.add
      .image(0, 0, TEXTURE.beamHalo)
      .setOrigin(0, 0.5)
      .setDepth(this.depth + 3)
      .setBlendMode(this.blendAdd)
      .setVisible(false);
    this.core = this.scene.add
      .image(0, 0, TEXTURE.beamCore)
      .setOrigin(0, 0.5)
      .setDepth(this.depth + 4)
      .setBlendMode(this.blendAdd)
      .setVisible(false);
    this.tip = this.scene.add
      .image(0, 0, TEXTURE.corona)
      .setDepth(this.depth + 5)
      .setBlendMode(this.blendAdd)
      .setVisible(false);
    /* A corona fica por CIMA de tudo: ela é a boca, e é onde o olho vai. */
    this.corona = this.scene.add
      .image(0, 0, TEXTURE.corona)
      .setDepth(this.depth + 6)
      .setBlendMode(this.blendAdd)
      .setVisible(false);
  }

  destroy(): void {
    for (const piece of [this.corona, this.heat, this.edge, this.halo, this.core, this.tip]) {
      piece?.destroy();
    }
    this.corona = null;
    this.heat = null;
    this.edge = null;
    this.halo = null;
    this.core = null;
    this.tip = null;
    for (const spark of this.sparks) {
      spark.destroy();
    }
    this.sparks.length = 0;
    this.litAtMs = null;
    this.fadeFromMs = null;
    this.last = null;
  }
}
